import React from 'react';
import { useTranslation } from 'react-i18next';
import { AddContactRow } from '@/components/ProfileMenu/NotificationsView/AddContactRow';
import { NotificationSubscriptionBanner } from '@/components/ProfileMenu/NotificationsView/NotificationSubscriptionBanner';
import { SectionFooterLink } from '@/components/ProfileMenu/NotificationsView/SectionFooterLink';
import { SettingRow } from '@/components/ProfileMenu/NotificationsView/SettingRow';
import { useProfileRestriction } from '@/components/ProfileRestriction/useProfileRestriction';
import { Checkbox } from '@/components/Checkbox';
import { LockClosed } from '@/components/Icons/LockClosed';
import { Toggle } from '@/components/Toggle';
import { useShowSubscriptionOffer } from '@/hooks/useShowSubscriptionOffer';
import type { EmailNotificationSettings } from '@/types/notifications';

interface Props {
  settings: EmailNotificationSettings;
  isSubscribed: boolean;
  hasEmail: boolean;
  viewOnly?: boolean;
  onChange: (settings: EmailNotificationSettings) => void;
  onChangeEmail: () => void;
  onPurchase: () => void;
}

export const EmailSection: React.FC<Props> = ({
  settings,
  isSubscribed,
  hasEmail,
  viewOnly = false,
  onChange,
  onChangeEmail,
  onPurchase,
}) => {
  const { t } = useTranslation();
  const { isRestricted } = useProfileRestriction();
  const showSubscriptionOffer = useShowSubscriptionOffer();
  const isCommunicationEnabled = settings.isCommunicationNotificationsEnabled;

  const isReadOnly = viewOnly || !hasEmail;

  const premiumSubtitle =
    isSubscribed || isRestricted
      ? ''
      : t('notificationSettings.subscriberOnly');

  const premiumToggle = (
    checked: boolean,
    label: string,
    onToggle: (value: boolean) => void,
  ) =>
    isSubscribed ? (
      <Toggle
        checked={checked}
        disabled={isReadOnly}
        label={label}
        onCheckedChange={onToggle}
      />
    ) : (
      <LockClosed className="size-6 shrink-0 text-subtle-foreground" />
    );

  const handleCommunicationChange = (value: boolean) =>
    onChange({
      ...settings,
      isCommunicationNotificationsEnabled: value,
      isIncomingMailMarkedAsRead: value
        ? settings.isIncomingMailMarkedAsRead
        : false,
    });

  return (
    <>
      {!viewOnly && !hasEmail && (
        <AddContactRow label={t('profileMenu.mail')} onAdd={onChangeEmail} />
      )}

      <div className="mx-4 flex flex-col rounded-xl bg-background px-4">
        <div className="flex items-center gap-3 py-4">
          <p className="min-w-0 flex-1 text-body-14 text-foreground">
            {t('notificationSettings.mailMessagesAndInvites')}
          </p>
          <Toggle
            checked={isCommunicationEnabled}
            disabled={isReadOnly}
            label={t('notificationSettings.mailMessagesAndInvites')}
            onCheckedChange={handleCommunicationChange}
          />
        </div>
        <div className="flex items-center gap-3 pb-4">
          <Checkbox
            checked={settings.isIncomingMailMarkedAsRead}
            disabled={isReadOnly || !isCommunicationEnabled}
            onChange={(value) =>
              onChange({ ...settings, isIncomingMailMarkedAsRead: value })
            }
          />
          <p className="min-w-0 flex-1 text-body-14 text-subtle-foreground">
            {t('notificationSettings.markRedirectsAsRead')}
          </p>
        </div>
      </div>

      {!isRestricted && (
        <p className="px-4 text-callout-16 text-foreground">
          {t('notificationSettings.subscriberSectionTitle')}
        </p>
      )}

      {showSubscriptionOffer && !viewOnly && (
        <NotificationSubscriptionBanner onPurchase={onPurchase} />
      )}

      <div
        className={`mx-4 flex flex-col rounded-xl bg-background ${viewOnly ? 'mb-4' : ''}`}
      >
        <SettingRow
          title={t('notificationSettings.diaryMessages')}
          subtitle={premiumSubtitle}
          hasDivider
          trailing={premiumToggle(
            settings.isDiaryAnnouncementNotificationsEnabled,
            t('notificationSettings.diaryMessages'),
            (value) =>
              onChange({
                ...settings,
                isDiaryAnnouncementNotificationsEnabled: value,
              }),
          )}
        />
        <SettingRow
          title={t('notificationSettings.dailySummary')}
          subtitle={premiumSubtitle}
          trailing={premiumToggle(
            settings.isEvaluationSummaryEnabled,
            t('notificationSettings.dailySummary'),
            (value) =>
              onChange({ ...settings, isEvaluationSummaryEnabled: value }),
          )}
        />
      </div>

      {!viewOnly && hasEmail && (
        <SectionFooterLink
          label={t('notificationSettings.changeEmail')}
          onClick={onChangeEmail}
        />
      )}
    </>
  );
};
